<template>
  <div class="materials-admin">
    <!-- Status / sincronização -->
    <div class="card">
      <div
        class="card-body d-flex flex-wrap align-items-center"
        style="gap: 16px"
      >
        <div>
          <div
            class="text-muted"
            style="font-size: 12px"
          >
            {{ $t('materials.lastSync') }}
          </div>
          <div style="font-weight: 600">
            {{ status && status.last_sync_at ? formatDate(status.last_sync_at) : $t('materials.neverSynced') }}
            <span
              v-if="status && status.last_sync_status"
              class="badge ml-50"
              :class="statusBadgeClass"
            >
              {{ statusLabel }}
            </span>
          </div>
          <div
            v-if="status && status.last_sync_message"
            class="text-danger"
            style="font-size: 11px"
          >
            {{ status.last_sync_message }}
          </div>
        </div>
        <div>
          <div
            class="text-muted"
            style="font-size: 12px"
          >
            {{ $t('materials.activeMaterials') }}
          </div>
          <div style="font-weight: 600">
            {{ status ? status.materials_count : '—' }}
          </div>
        </div>
        <div
          class="ml-auto d-flex"
          style="gap: 8px"
        >
          <button
            v-if="$hasAbility('node.admin')"
            type="button"
            class="btn btn-primary btn-sm"
            :disabled="syncing || !(status && status.enabled)"
            @click="syncNow(false)"
          >
            <span
              v-if="syncing"
              class="spinner-border spinner-border-sm mr-50"
            />
            <feather-icon
              v-else
              icon="RefreshCwIcon"
              size="14"
              class="mr-50"
            />
            {{ $t('materials.syncNow') }}
          </button>
          <button
            v-if="$hasAbility('node.admin')"
            type="button"
            class="btn btn-outline-secondary btn-sm"
            :disabled="syncing || !(status && status.enabled)"
            :title="$t('materials.fullSyncTooltip')"
            @click="syncNow(true)"
          >
            {{ $t('materials.fullSyncButton') }}
          </button>
        </div>
      </div>
      <div
        v-if="status && !status.enabled"
        class="card-footer text-warning"
        style="font-size: 12px"
      >
        {{ $t('materials.integrationDisabled') }}
      </div>
    </div>

    <!-- Catálogo -->
    <div class="card">
      <div class="card-body">
        <div
          v-if="$hasAbility('node.ver')"
          class="d-flex flex-wrap align-items-end mb-1"
          style="gap: 12px"
        >
          <div style="flex: 1; min-width: 220px">
            <label style="font-size: 12px">{{ $t('materials.searchLabel') }}</label>
            <input
              v-model="filters.q"
              class="form-control"
              type="text"
              :placeholder="$t('materials.searchPlaceholder')"
              @input="onSearch"
            >
          </div>
          <div>
            <label style="font-size: 12px">{{ $t('materials.situationLabel') }}</label>
            <select
              v-model="filters.active"
              class="form-control"
              @change="reload"
            >
              <option value="1">
                {{ $t('materials.situationActive') }}
              </option>
              <option value="0">
                {{ $t('materials.situationInactive') }}
              </option>
              <option value="">
                {{ $t('materials.situationAll') }}
              </option>
            </select>
          </div>
        </div>

        <div
          v-if="loading"
          class="text-center py-3"
        >
          <span class="spinner-border spinner-border-sm" /> {{ $t('materials.loading') }}
        </div>

        <div
          v-else
          class="table-responsive"
        >
          <table class="table table-sm">
            <thead>
              <tr>
                <th>{{ $t('materials.table.code') }}</th>
                <th>{{ $t('materials.table.material') }}</th>
                <th>{{ $t('materials.table.tab') }}</th>
                <th>{{ $t('materials.table.unit') }}</th>
                <th class="text-center">
                  {{ $t('materials.table.situation') }}
                </th>
                <th class="text-center">
                  {{ $t('materials.table.cbos') }}
                </th>
                <th />
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="m in materials"
                :key="m.id"
              >
                <td class="text-nowrap">
                  {{ m.code || '—' }}
                </td>
                <td>
                  {{ m.name }}
                  <span
                    v-if="m.controlled"
                    class="badge badge-light-danger ml-50"
                  >{{ $t('materials.table.controlled') }}</span>
                </td>
                <td>{{ m.tab ? m.tab.title : '—' }}</td>
                <td>{{ m.unit || '—' }}</td>
                <td class="text-center">
                  <span
                    class="badge"
                    :class="m.active ? 'badge-light-success' : 'badge-light-secondary'"
                  >
                    {{ m.active ? $t('common.active') : $t('common.inactive') }}
                  </span>
                </td>
                <td class="text-center">
                  <span
                    v-if="m.cbos && m.cbos.length"
                    class="badge badge-light-primary"
                  >
                    {{ m.cbos.length }}
                  </span>
                  <span
                    v-else
                    class="text-muted"
                    style="font-size: 11px"
                  >{{ $t('materials.table.all') }}</span>
                </td>
                <td class="text-right">
                  <button
                    v-if="$hasAbility('node.admin')"
                    type="button"
                    class="btn btn-outline-primary btn-sm"
                    @click="openCbos(m)"
                  >
                    <feather-icon
                      icon="UsersIcon"
                      size="14"
                      class="mr-25"
                    />
                    {{ $t('materials.table.cbos') }}
                  </button>
                </td>
              </tr>
              <tr v-if="!materials.length">
                <td
                  colspan="7"
                  class="text-center text-muted py-2"
                >
                  {{ $t('materials.table.empty') }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Paginação simples -->
        <div
          v-if="$hasAbility('node.ver')"
          class="d-flex align-items-center justify-content-between mt-1"
        >
          <small class="text-muted">{{ total }} {{ $t('materials.materialsSuffix') }}</small>
          <div
            class="d-flex align-items-center"
            style="gap: 8px"
          >
            <button
              type="button"
              class="btn btn-outline-secondary btn-sm"
              :disabled="page <= 1"
              @click="changePage(page - 1)"
            >
              {{ $t('materials.previousButton') }}
            </button>
            <small>{{ $t('materials.pageLabel', { page, lastPage }) }}</small>
            <button
              type="button"
              class="btn btn-outline-secondary btn-sm"
              :disabled="page >= lastPage"
              @click="changePage(page + 1)"
            >
              {{ $t('materials.nextButton') }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal: vincular CBOs (b-modal é global via ModalPlugin) -->
    <b-modal
      v-model="showCboModal"
      :title="$t('materials.cboModal.title')"
      :ok-title="$t('common.save')"
      :cancel-title="$t('common.cancel')"
      centered
      :busy="savingCbos"
      @ok.prevent="saveCbos"
    >
      <p style="font-size: 13px">
        <strong>{{ cboMaterial ? cboMaterial.name : '' }}</strong><br>
        <span
          class="text-muted"
          style="font-size: 12px"
        >
          {{ $t('materials.cboModal.hint') }}
        </span>
      </p>
      <v-select
        v-model="selectedCbos"
        :options="cboCatalog"
        :reduce="(c) => c.id"
        label="cbo_name"
        multiple
        :loading="loadingCboCatalog"
        :placeholder="$t('materials.cboModal.placeholder')"
      />
    </b-modal>
  </div>
</template>

<script>
export default {
  name: 'MaterialsAdmin',
  data() {
    return {
      nodeId: this.$route.params.nodeId,
      status: null,
      syncing: false,
      loading: false,
      materials: [],
      total: 0,
      page: 1,
      perPage: 25,
      filters: { q: '', active: '1' },
      searchTimer: null,
      // CBOs
      showCboModal: false,
      cboMaterial: null,
      cboCatalog: [],
      loadingCboCatalog: false,
      selectedCbos: [],
      savingCbos: false,
    }
  },
  computed: {
    lastPage() {
      return Math.max(1, Math.ceil(this.total / this.perPage))
    },
    statusBadgeClass() {
      const s = this.status && this.status.last_sync_status
      if (s === 'success') return 'badge-light-success'
      if (s === 'error') return 'badge-light-danger'
      if (s === 'running') return 'badge-light-warning'
      return 'badge-light-secondary'
    },
    statusLabel() {
      const map = {
        success: this.$t('materials.statusLabels.success'),
        error: this.$t('materials.statusLabels.error'),
        running: this.$t('materials.statusLabels.running'),
      }
      return map[this.status && this.status.last_sync_status] || '—'
    },
  },
  mounted() {
    this.fetchStatus()
    this.reload()
  },
  methods: {
    formatDate(val) {
      if (!val) return '—'
      return new Date(val).toLocaleString('pt-BR', {
        day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
      })
    },
    fetchStatus() {
      this.$http
        .get(`${this.$store.state.api}materials/sync/status`, { params: { node_id: this.nodeId } })
        .then(r => { this.status = r.data.data })
        .catch(() => {})
    },
    fetchMaterials() {
      this.loading = true
      this.$http
        .get(`${this.$store.state.api}materials`, {
          params: {
            node_id: this.nodeId,
            page: this.page,
            limit: this.perPage,
            q: this.filters.q || undefined,
            active: this.filters.active !== '' ? this.filters.active : undefined,
          },
        })
        .then(r => {
          this.materials = r.data.data || []
          this.total = r.data.count || 0
        })
        .catch(() => { this.materials = []; this.total = 0 })
        .finally(() => { this.loading = false })
    },
    reload() {
      this.page = 1
      this.fetchMaterials()
    },
    changePage(p) {
      this.page = p
      this.fetchMaterials()
    },
    onSearch() {
      if (this.searchTimer) clearTimeout(this.searchTimer)
      this.searchTimer = setTimeout(() => this.reload(), 400)
    },
    syncNow(full) {
      this.syncing = true
      this.$http
        .post(`${this.$store.state.api}materials/sync`, { node_id: this.nodeId, full })
        .then(r => {
          if (r.data && r.data.ok) {
            const c = r.data.counters || {}
            this.$message(this.$t('materials.messages.syncSuccessTitle'), this.$t('materials.messages.syncSuccessMessage', { materials: c.materials || 0, tabs: c.tabs || 0 }), 'success')
            this.status = r.data.status
            this.fetchMaterials()
          } else {
            this.$message(this.$t('materials.messages.syncWarningTitle'), (r.data && r.data.message) || this.$t('materials.messages.syncNotCompleted'), 'warning')
          }
        })
        .catch(e => {
          const msg = e.response && e.response.data && e.response.data.message
            ? e.response.data.message : this.$t('materials.messages.syncFailed')
          this.$message(this.$t('materials.messages.syncErrorTitle'), msg, 'error')
          if (e.response && e.response.data && e.response.data.status) this.status = e.response.data.status
        })
        .finally(() => { this.syncing = false })
    },
    loadCboCatalog() {
      if (this.cboCatalog.length) return
      this.loadingCboCatalog = true
      this.$http
        .get(`${this.$store.state.api}cbos`, { params: { paginated: false } })
        .then(r => { this.cboCatalog = r.data.data || r.data || [] })
        .catch(() => { this.cboCatalog = [] })
        .finally(() => { this.loadingCboCatalog = false })
    },
    openCbos(material) {
      this.cboMaterial = material
      this.selectedCbos = (material.cbos || []).map(c => c.id)
      this.loadCboCatalog()
      this.showCboModal = true
    },
    saveCbos() {
      this.savingCbos = true
      this.$http
        .post(`${this.$store.state.api}materials/${this.cboMaterial.id}/cbos`, {
          node_id: this.nodeId,
          cbo_ids: this.selectedCbos,
        })
        .then(r => {
          const cbos = r.data.data || []
          this.$set(this.cboMaterial, 'cbos', cbos)
          this.showCboModal = false
          this.$message(this.$t('materials.messages.syncSuccessTitle'), this.$t('materials.messages.cbosUpdated'), 'success')
        })
        .catch(() => this.$message(this.$t('materials.messages.syncErrorTitle'), this.$t('materials.messages.cbosSaveError'), 'error'))
        .finally(() => { this.savingCbos = false })
    },
  },
}
</script>
